import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";
import { History, ExternalLink, MessageCircle, Loader2, Package, CheckCircle2, Circle, Trash2 } from "lucide-react";

const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;
const ZONE = { napoli: "Napoli e Provincia", italia: "Resto d'Italia", ritiro: "Ritiro di persona" };

export default function StoricoVenditeRapide() {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/vendite-rapide").then(({ data }) => setLista(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const reinviaWhatsapp = (v) => {
    const tel = (v.whatsapp || "").replace(/\D/g, "");
    if (tel.length < 8) return toast.error("Numero WhatsApp non presente per questa vendita.");
    if (!v.invoice_url) return toast.error("Link di pagamento non disponibile.");
    const msg = `Ciao! Ecco il link sicuro per completare il pagamento del tuo ordine personalizzato: ${v.invoice_url}. Grazie dallo staff!`;
    window.open(`https://wa.me/${tel}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const togglePagato = async (v) => {
    const nuovo = v.stato_pagamento === "pagato" ? "non_pagato" : "pagato";
    try { await api.patch(`/vendite-rapide/${v.id}/stato`, { stato: nuovo }); load(); }
    catch { toast.error("Errore aggiornamento stato."); }
  };

  const eliminaVendita = async (v) => {
    if (!window.confirm("Eliminare questa vendita dallo storico? L'operazione è irreversibile.")) return;
    try { await api.delete(`/vendite-rapide/${v.id}`); toast.success("Vendita eliminata."); load(); }
    catch { toast.error("Errore nell'eliminazione."); }
  };

  const fmtData = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

  return (
    <Layout>
      <div className="mb-6 flex items-center gap-2.5">
        <div className="w-10 h-10 rounded-xl bg-violet-400/15 border border-violet-400/30 flex items-center justify-center">
          <History className="w-5 h-5 text-violet-300" />
        </div>
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-100 tracking-tight">Storico Vendite Rapide</h1>
          <p className="text-slate-500 text-sm">Ordini Shopify creati via link, con re-invio WhatsApp e stato pagamento</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Caricamento...</div>
      ) : lista.length === 0 ? (
        <p data-testid="storico-empty" className="text-slate-500 text-sm">Nessuna vendita ancora registrata.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="storico-list">
          {lista.map((v) => {
            const pagato = v.stato_pagamento === "pagato";
            return (
              <div key={v.id} data-testid={`vendita-${v.id}`} className="glass rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <span className="font-mono-tech text-slate-300 text-sm">{v.shopify_name || "—"}</span>
                    <span className="text-xs text-slate-500 ml-2">{fmtData(v.created_at)}</span>
                  </div>
                  <button data-testid={`toggle-pagato-${v.id}`} onClick={() => togglePagato(v)} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${pagato ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" : "bg-slate-700/40 text-slate-400 border-slate-600"}`}>
                    {pagato ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />} {pagato ? "Pagato" : "Non pagato"}
                  </button>
                </div>
                <div className="flex gap-3">
                  {v.immagine_url && <img src={v.immagine_url} alt="prodotto" className="w-16 h-16 rounded-lg object-cover border border-slate-700 shrink-0" />}
                  <div className="flex-1 min-w-0 space-y-1">
                    {(v.righe || []).map((r, i) => (
                      <div key={i} className="text-sm text-slate-300 flex items-center gap-1 truncate">
                        <Package className="w-3 h-3 text-slate-500 shrink-0" /> {r.quantita}× {r.sku_neutro || r.tipo_capo}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm border-t border-slate-800/60 pt-2">
                  <span className="text-slate-500">{ZONE[v.zona]} {v.whatsapp ? `· ${v.whatsapp}` : ""}</span>
                  <span className="font-mono-tech text-emerald-300 font-bold">{eur(v.totale)}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {v.invoice_url && <a data-testid={`invoice-${v.id}`} href={v.invoice_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/25"><ExternalLink className="w-3.5 h-3.5" /> Link Shopify</a>}
                  {v.whatsapp && <button data-testid={`wa-${v.id}`} onClick={() => reinviaWhatsapp(v)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/30 hover:bg-[#25D366]/25"><MessageCircle className="w-3.5 h-3.5" /> Re-invia link</button>}
                  <button data-testid={`elimina-${v.id}`} onClick={() => eliminaVendita(v)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25 ml-auto"><Trash2 className="w-3.5 h-3.5" /> Elimina</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
